import TrialSiteImage from '@components/app/TrialSummaryCard/TrialSiteImage.client'
import { Icon } from '@components/primitives/Icon'
import ClientLinkWithServerChildren from '@components/primitives/Link/ClientLinkWithServerChildren.client'
import { Text } from '@components/primitives/Text'
import { faAwardSimple } from '@fortawesome/pro-solid-svg-icons/faAwardSimple'
import { faBarsProgress } from '@fortawesome/pro-solid-svg-icons/faBarsProgress'
import { faList } from '@fortawesome/pro-solid-svg-icons/faList'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import Routes from '@lib/routes'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import calculateStringValue from '@lib/utilities/number/calculateStringValue'
import pluralize from '@lib/utilities/text/pluralize'
import type { DoctorPageData } from '@modules/principalInvestigators/getDoctorPageData'
import { getPhaseFromPhaseList } from '@modules/trials/helpers/getPhaseFromPhaseList'
import { getTrialBriefSummary } from '@modules/trials/helpers/getTrialBriefSummary'

export type TrialForDoctorPage =
  NonNullable<DoctorPageData>['uniqueTrialsByNctId'][0]

type NarrowTrialForSummaryCard = Pick<
  TrialForDoctorPage,
  | 'abbreviatedTitle'
  | 'awardBadges'
  | 'conjuredTitle'
  | 'nctId'
  | 'parsedEligibilityCriteria'
  | 'phaseList'
  | 'slug'
  | 'nlpBriefSummary'
  | 'briefSummary'
>

export const SimpleTrialSummaryCard = ({
  trial,
}: {
  trial: NarrowTrialForSummaryCard
}) => {
  const preferredTitle = trial.abbreviatedTitle ?? trial.conjuredTitle!
  const [treatment, condition] = preferredTitle.split(' for ')

  return (
    <ClientLinkWithServerChildren
      href={trial.slug ? DynamicRoutes.trial(trial.slug) : Routes.search}
      trackingEvent={TrackingEvents.TRIAL_LISTING_CLICKED}
    >
      <div className='rounded-xl border border-neutral200 p-6 hover:bg-neutral100'>
        <div className={`mb-3 flex w-full flex-row items-start justify-start`}>
          <TrialSiteImage
            className='size-16 overflow-clip rounded-md'
            containerClass='size-16'
            height={64}
            isAboveFold={false}
            trialDigitOffset={calculateStringValue(trial.nctId)}
            trialLocations={[]}
            width={64}
          />
          <div className='flex flex-col px-4'>
            <Text
              className='inline text-black'
              element='h3'
              styleName='h5'
              title={treatment}
              value={treatment}
            />

            <Text
              className='inline text-neutral600'
              element='h4'
              styleName='p-small'
              title={condition}
              value={`for ${condition}`}
            />
          </div>
        </div>
        <Text
          className='mb-3 line-clamp-2 h-auto text-neutral600'
          element='h5'
          styleName='p-tiny'
          value={getTrialBriefSummary({
            ...trial,
            latestDavinciBriefSummary: undefined, // getTrialBriefSummary needs to have this value, but it is from the algolia trial and missing here
          })}
        />
        <IconsSection trial={trial} />
      </div>
    </ClientLinkWithServerChildren>
  )
}

const IconsSection = ({ trial }: { trial: NarrowTrialForSummaryCard }) => {
  const criteriaCount = trial.parsedEligibilityCriteria?.filter(
    (ie) => ie.isPreScreenerEnabled && ie.isInclusion,
  ).length

  const phase = getPhaseFromPhaseList(trial.phaseList)

  return (
    <>
      <div className='flex flex-row flex-wrap items-center gap-x-4 gap-y-2'>
        <Text className='text-black' styleName='p-small'>
          <Icon className='mr-2 text-primary400' icon={faUser} />
          Recruiting
        </Text>

        <Text className='text-black' styleName='p-small'>
          <Icon className='mr-2 text-hope600' icon={faAwardSimple} />
          {pluralize('award', trial.awardBadges.length, {
            includeCount: true,
          })}
        </Text>

        <Text className='text-black' styleName='p-small'>
          <Icon className='mr-2 text-turquoise500' icon={faBarsProgress} />{' '}
          {phase}
        </Text>

        {criteriaCount > 0 && (
          <Text className='text-black' styleName='p-small'>
            <Icon className='mr-2 text-success500' icon={faList} />
            {criteriaCount} criteria
          </Text>
        )}
      </div>
    </>
  )
}
